import { Check, ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  useCommandContext,
} from '@/components/ui/command'

import { cn } from '@/lib/utils'

export interface MultiSelectOption {
  value: string
  label: string
}

interface FilteredItemsProps {
  options: MultiSelectOption[]
  selected: string[]
  onToggle: (value: string) => void
}

function FilteredItems({ options, selected, onToggle }: FilteredItemsProps) {
  const { query } = useCommandContext()
  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options

  if (!filtered.length) {
    return <CommandEmpty>Sin resultados.</CommandEmpty>
  }

  return (
    <>
      {filtered.map((option) => (
        <CommandItem key={option.value} onClick={() => onToggle(option.value)}>
          <Check
            className={cn(
              'mr-2 h-4 w-4 shrink-0',
              selected.includes(option.value) ? 'opacity-100' : 'opacity-0'
            )}
          />
          {option.label}
        </CommandItem>
      ))}
    </>
  )
}

interface MultiSelectProps {
  options: MultiSelectOption[]
  selected: string[]
  onChange: (values: string[]) => void
  placeholder?: string
  className?: string
}

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = 'Seleccionar...',
  className,
}: MultiSelectProps) {
  const toggle = (value: string) => {
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value]
    )
  }

  const label =
    selected.length === 0
      ? placeholder
      : selected.length === 1
        ? (options.find((o) => o.value === selected[0])?.label ?? `1 seleccionado`)
        : `${selected.length} seleccionados`

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'h-10 min-w-[12rem] justify-between border-border bg-background font-normal',
            className
          )}
        >
          <span className="truncate">{label}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-0">
        <Command>
          <CommandInput placeholder="Buscar..." />
          <CommandList>
            <CommandGroup>
              <FilteredItems options={options} selected={selected} onToggle={toggle} />
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
